import { captureException } from '@sentry/react'
import { Form, Formik, FormikHelpers } from 'formik'
import { ReactNode, useState } from 'react'
import { Button } from '~/components/button'
import GoogleAuthButton from '~/components/google-identity/google-auth-button'
import { TextInputField } from '~/components/text-input'
import { Meta } from '~/components/typography'
import { routes } from '~/config/routes'
import { useToasts } from '~/context/toasts'
import { Invitation } from '~/hooks/use-invitation'
import { useRecaptcha } from '~/hooks/use-recaptcha'
import { useSignInUser } from '~/hooks/use-sign-in-user'
import { AuthLayout } from '~/layouts/auth-layout'
import { useSignUpWithEmailMutation } from '~/pages/sign-up/graphql/sign-up-with-email'
import { useForm, Values } from '~/pages/sign-up/hooks/use-form'
import { colors } from '~/styles/colors'
import { apiError } from '~/utils/api-error'

type Props = {
  invitation: Invitation
}

export const InvitationSignUp = ({ invitation }: Props) => {
  const [signUpWithEmail] = useSignUpWithEmailMutation()
  const signInUser = useSignInUser()
  const recaptcha = useRecaptcha()
  const toasts = useToasts()
  const [authorizing] = useState(false)
  const { initialValues, schema } = useForm(invitation.email)

  const handleSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
    try {
      const recaptchaResponse = await recaptcha.execute()

      const { data } = await signUpWithEmail({
        variables: {
          input: {
            email: values.email,
            password: values.password,
            recaptchaResponse,
            invitation: invitation.input,
          },
        },
      })

      if (!data) {
        throw new Error('Invitation sign-up returned no data')
      }

      await signInUser(data.signUpWithEmail.accessToken)
    } catch (error: any) {
      let handled = false

      apiError(error, {
        email_already_exists: () => {
          handled = true
          helpers.setFieldError('email', 'Email already exists')
        },
        invalid_invitation_token: () => {
          handled = true
          helpers.setFieldError('email', 'Invitation is not valid for this email')
        },
      })

      if (!handled) {
        captureException(error)
        toasts.error('Something went wrong. Please try again.')
      }

      recaptcha.reset()
    }
  }

  const subheading: ReactNode = (
    <>
      <strong>{invitation.invitedBy}</strong> invited you to join{' '}
      <strong>{invitation.workspaceName}</strong>
    </>
  )

  return (
    <AuthLayout heading="Sign Up to Kindest" subheading={subheading}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={schema}
        validateOnBlur={false}
      >
        {({ isSubmitting }) => (
          <>
            <div className="flex justify-center">
              <GoogleAuthButton type="sign-up" />
            </div>

            <AuthLayout.Divider>or</AuthLayout.Divider>

            <Form>
              <TextInputField
                label="Email"
                name="email"
                autoComplete="email"
                disabled
                marginBottom={20}
                large
              />
              <TextInputField
                label="Password"
                name="password"
                type="password"
                autoComplete="new-password"
                marginBottom={32}
                disabled={authorizing}
                large
              />
              <Button
                type="submit"
                loading={isSubmitting}
                size="large"
                width="100%"
                disabled={authorizing}
              >
                Get Started
              </Button>

              <Meta regular marginTop={20} color={colors.slateGray} style={{ textAlign: 'center' }}>
                By signing up, you agree to our{' '}
                <a
                  className="hover:text-dark hover:underline"
                  href={routes.legal.terms}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
                <br />
                and{' '}
                <a
                  className="hover:text-dark hover:underline"
                  href={routes.legal.privacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </Meta>
            </Form>
          </>
        )}
      </Formik>
    </AuthLayout>
  )
}
